import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Link,
  List,
  ListItem,
  ListItemSecondaryAction,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import axios from 'axios';

const Admin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalUsers: 0,
    totalAmount: 0,
    statusDistribution: {}
  });

  // Advanced filters
  const [creatorFilter, setCreatorFilter] = useState('');
  const [amountFilter, setAmountFilter] = useState({
    type: 'all', // 'all', 'greater', 'less', 'between'
    value: '',
    min: '',
    max: ''
  });
  const [dateFilter, setDateFilter] = useState({
    type: 'all', // 'all', 'after', 'before', 'between'
    value: '',
    start: '',
    end: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [allRecords, searchTerm, statusFilter, creatorFilter, amountFilter, dateFilter]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [recordsResponse, usersResponse, statsResponse] = await Promise.all([
        axios.get('/api/fraud-records/all'),
        axios.get('/api/users'),
        axios.get('/api/fraud-records/stats')
      ]);

      setAllRecords(recordsResponse.data.records || []);
      setStats({
        totalRecords: recordsResponse.data.records?.length || 0,
        totalUsers: usersResponse.data.users?.length || 0,
        totalAmount: recordsResponse.data.records?.reduce((sum, record) => 
          sum + (parseFloat(record.callDetails?.totalAmount) || 0), 0) || 0,
        statusDistribution: statsResponse.data.distribution?.status || {}
      });
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const filterRecords = () => {
    let filtered = allRecords;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.callDetails?.scamType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.callDetails?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.createdBy?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.createdBy?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    // Creator filter
    if (creatorFilter) {
      filtered = filtered.filter(record =>
        record.createdBy?.username?.toLowerCase().includes(creatorFilter.toLowerCase()) ||
        record.createdBy?.email?.toLowerCase().includes(creatorFilter.toLowerCase())
      );
    }

    // Amount filter
    if (amountFilter.type !== 'all') {
      filtered = filtered.filter(record => {
        const amount = parseFloat(record.callDetails?.totalAmount) || 0;
        switch (amountFilter.type) {
          case 'greater':
            return amount > parseFloat(amountFilter.value);
          case 'less':
            return amount < parseFloat(amountFilter.value);
          case 'between':
            const min = parseFloat(amountFilter.min);
            const max = parseFloat(amountFilter.max);
            return amount >= min && amount <= max;
          default:
            return true;
        }
      });
    }

    // Date filter
    if (dateFilter.type !== 'all') {
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.createdAt);
        switch (dateFilter.type) {
          case 'after':
            return recordDate >= new Date(dateFilter.value);
          case 'before':
            return recordDate <= new Date(dateFilter.value);
          case 'between':
            const startDate = new Date(dateFilter.start);
            const endDate = new Date(dateFilter.end);
            return recordDate >= startDate && recordDate <= endDate;
          default:
            return true;
        }
      });
    }

    setFilteredRecords(filtered);
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setDetailDialogOpen(true);
  };

  const handleMenuClick = (event, recordId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(recordId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleEditRecord = (recordId) => {
    navigate(`/fraud-records/${recordId}`);
    handleMenuClose();
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        await axios.delete(`/api/fraud-records/${recordId}`);
        setAllRecords(prev => prev.filter(record => record._id !== recordId));
        handleMenuClose();
      } catch (err) {
        console.error('Error deleting record:', err);
        setError('Failed to delete record');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#ffa500';
      case 'in-progress': return '#6464ff';
      case 'done': return '#28aa50';
      default: return '#666666';
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('audio/')) return '🎵';
    if (mimeType?.startsWith('image/')) return '🖼️';
    if (mimeType?.startsWith('video/')) return '🎥';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType?.includes('document') || mimeType?.includes('word')) return '📝';
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('text')) return '📋';
    return '📎';
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCreatorFilter('');
    setAmountFilter({ type: 'all', value: '', min: '', max: '' });
    setDateFilter({ type: 'all', value: '', start: '', end: '' });
    setShowAdvancedFilters(false);
  };

  const exportToCSV = () => {
    const headers = [
      'Created By',
      'Email',
      'Scam Type',
      'Amount',
      'Currency',
      'Status',
      'Description',
      'Contact Type',
      'Contact Date',
      'Created Date',
      'Drive Links Count',
      'Bank Accounts Count'
    ];

    const csvData = filteredRecords.map(record => [
      record.createdBy?.username || 'Unknown',
      record.createdBy?.email || 'No email',
      record.callDetails?.scamType || 'N/A',
      record.callDetails?.totalAmount || 0,
      record.callDetails?.currency || 'USD',
      record.status || 'new',
      record.callDetails?.description || 'No description',
      record.callDetails?.callType || 'N/A',
      record.callDetails?.callDate || 'N/A',
      format(new Date(record.createdAt), 'yyyy-MM-dd HH:mm:ss'),
      record.driveLinks?.length || 0,
      record.bankAccounts?.length || 0
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `fraud_records_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchAllData} startIcon={<RefreshIcon />}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Manage all fraud records and users across the system
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#24252a', border: '1px solid #444' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SecurityIcon sx={{ color: '#6464ff', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Total Records
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                {stats.totalRecords}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#24252a', border: '1px solid #444' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PersonIcon sx={{ color: '#28aa50', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                {stats.totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#24252a', border: '1px solid #444' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#ffa500', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Total Amount
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                ${stats.totalAmount.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#24252a', border: '1px solid #444' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AssessmentIcon sx={{ color: '#c83232', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#ffffff' }}>
                  Active Cases
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700 }}>
                {stats.statusDistribution['in-progress'] || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ bgcolor: '#24252a', border: '1px solid #444', mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search records, users, or descriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#b0b0b0' }} />
                    </InputAdornment>
                  ),
                  sx: { color: '#ffffff', bgcolor: '#2a2a2a' }
                }}
                InputLabelProps={{ sx: { color: '#bdbdbd' } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                select
                label="Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ color: '#ffffff', bgcolor: '#2a2a2a' }}
                InputLabelProps={{ sx: { color: '#bdbdbd' } }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="new">New</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ 
                  borderColor: '#666', 
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: '#888',
                    backgroundColor: '#2a2a2a'
                  }
                }}
              >
                {showAdvancedFilters ? 'Hide Filters' : 'Advanced'}
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportToCSV}
                sx={{ 
                  borderColor: '#666', 
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: '#888',
                    backgroundColor: '#2a2a2a'
                  }
                }}
              >
                Export CSV
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchAllData}
                sx={{ 
                  borderColor: '#666', 
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: '#888',
                    backgroundColor: '#2a2a2a'
                  }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <Box sx={{ 
              mt: 3, 
              pt: 3, 
              borderTop: '1px solid #333',
              backgroundColor: '#1e1e1e',
              borderRadius: 1,
              px: 2,
              py: 2
            }}>
              <Typography variant="subtitle2" sx={{ 
                color: '#b0b0b0', 
                mb: 2, 
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.75rem'
              }}>
                Advanced Filters
              </Typography>
              <Grid container spacing={3} alignItems="flex-end">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Creator Filter"
                    placeholder="Filter by creator name or email"
                    value={creatorFilter}
                    onChange={(e) => setCreatorFilter(e.target.value)}
                    InputProps={{ 
                      sx: { 
                        color: '#ffffff', 
                        bgcolor: '#2a2a2a',
                        fontSize: '0.875rem'
                      } 
                    }}
                    InputLabelProps={{ 
                      sx: { 
                        color: '#b0b0b0',
                        fontSize: '0.875rem'
                      } 
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ 
                      color: '#b0b0b0',
                      fontSize: '0.875rem'
                    }}>Amount Filter</InputLabel>
                    <Select
                      value={amountFilter.type}
                      onChange={(e) => setAmountFilter(prev => ({ ...prev, type: e.target.value }))}
                      sx={{ 
                        color: '#ffffff', 
                        bgcolor: '#2a2a2a',
                        fontSize: '0.875rem'
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Amounts</MenuItem>
                      <MenuItem value="greater" sx={{ fontSize: '0.875rem' }}>Greater Than</MenuItem>
                      <MenuItem value="less" sx={{ fontSize: '0.875rem' }}>Less Than</MenuItem>
                      <MenuItem value="between" sx={{ fontSize: '0.875rem' }}>Between</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  {amountFilter.type === 'between' ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Min Amount"
                        type="number"
                        value={amountFilter.min}
                        onChange={(e) => setAmountFilter(prev => ({ ...prev, min: e.target.value }))}
                        InputProps={{ 
                          sx: { 
                            color: '#ffffff', 
                            bgcolor: '#2a2a2a',
                            fontSize: '0.875rem'
                          } 
                        }}
                        InputLabelProps={{ 
                          sx: { 
                            color: '#b0b0b0',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Max Amount"
                        type="number"
                        value={amountFilter.max}
                        onChange={(e) => setAmountFilter(prev => ({ ...prev, max: e.target.value }))}
                        InputProps={{ 
                          sx: { 
                            color: '#ffffff', 
                            bgcolor: '#2a2a2a',
                            fontSize: '0.875rem'
                          } 
                        }}
                        InputLabelProps={{ 
                          sx: { 
                            color: '#b0b0b0',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                    </Box>
                  ) : amountFilter.type !== 'all' ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Amount"
                      type="number"
                      value={amountFilter.value}
                      onChange={(e) => setAmountFilter(prev => ({ ...prev, value: e.target.value }))}
                      InputProps={{ 
                        sx: { 
                          color: '#ffffff', 
                          bgcolor: '#2a2a2a',
                          fontSize: '0.875rem'
                        } 
                      }}
                      InputLabelProps={{ 
                        sx: { 
                          color: '#b0b0b0',
                          fontSize: '0.875rem'
                        } 
                      }}
                    />
                  ) : null}
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ 
                      color: '#b0b0b0',
                      fontSize: '0.875rem'
                    }}>Date Filter</InputLabel>
                    <Select
                      value={dateFilter.type}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, type: e.target.value }))}
                      sx={{ 
                        color: '#ffffff', 
                        bgcolor: '#2a2a2a',
                        fontSize: '0.875rem'
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.875rem' }}>All Dates</MenuItem>
                      <MenuItem value="after" sx={{ fontSize: '0.875rem' }}>After Date</MenuItem>
                      <MenuItem value="before" sx={{ fontSize: '0.875rem' }}>Before Date</MenuItem>
                      <MenuItem value="between" sx={{ fontSize: '0.875rem' }}>Between Dates</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {dateFilter.type === 'between' ? (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Start Date"
                        type="date"
                        value={dateFilter.start}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                        InputLabelProps={{ 
                          shrink: true, 
                          sx: { 
                            color: '#b0b0b0',
                            fontSize: '0.875rem'
                          } 
                        }}
                        InputProps={{ 
                          sx: { 
                            color: '#ffffff',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="End Date"
                        type="date"
                        value={dateFilter.end}
                        onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                        InputLabelProps={{ 
                          shrink: true, 
                          sx: { 
                            color: '#b0b0b0',
                            fontSize: '0.875rem'
                          } 
                        }}
                        InputProps={{ 
                          sx: { 
                            color: '#ffffff',
                            fontSize: '0.875rem'
                          } 
                        }}
                      />
                    </Box>
                  </Grid>
                ) : dateFilter.type !== 'all' ? (
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Date"
                      type="date"
                      value={dateFilter.value}
                      onChange={(e) => setDateFilter(prev => ({ ...prev, value: e.target.value }))}
                      InputLabelProps={{ 
                        shrink: true, 
                        sx: { 
                          color: '#b0b0b0',
                          fontSize: '0.875rem'
                        } 
                      }}
                      InputProps={{ 
                        sx: { 
                          color: '#ffffff',
                          fontSize: '0.875rem'
                        } 
                      }}
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearAllFilters}
                    sx={{ 
                      borderColor: '#666', 
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#888',
                        backgroundColor: '#2a2a2a'
                      }
                    }}
                  >
                    Clear All
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card sx={{ bgcolor: '#24252a', border: '1px solid #444' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
            All Fraud Records ({filteredRecords.length})
          </Typography>
          
          <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Created By</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Scam Type</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Created Date</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Drive Links</TableCell>
                  <TableCell sx={{ color: '#ffffff', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#6464ff' }}>
                          {record.createdBy?.username?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ color: '#ffffff' }}>
                            {record.createdBy?.username || 'Unknown User'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                            {record.createdBy?.email || 'No email'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff' }}>
                      {record.callDetails?.scamType || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff' }}>
                      ${record.callDetails?.totalAmount || 0}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={record.status || 'new'}
                        sx={{
                          bgcolor: getStatusColor(record.status),
                          color: '#ffffff',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff' }}>
                      {format(new Date(record.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {record.driveLinks?.slice(0, 3).map((link, index) => (
                          <Tooltip key={index} title={`${link.title} - Click to open`}>
                            <IconButton
                              size="small"
                              onClick={() => window.open(link.url, '_blank')}
                              sx={{ 
                                color: '#6464ff',
                                '&:hover': { color: '#8484ff' }
                              }}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        {record.driveLinks?.length > 3 && (
                          <Tooltip title={`${record.driveLinks.length - 3} more links`}>
                            <Typography sx={{ color: '#b0b0b0', fontSize: '0.75rem', alignSelf: 'center' }}>
                              +{record.driveLinks.length - 3}
                            </Typography>
                          </Tooltip>
                        )}
                        {(!record.driveLinks || record.driveLinks.length === 0) && (
                          <Typography sx={{ color: '#666', fontSize: '0.75rem', fontStyle: 'italic' }}>
                            No links
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, record._id)}
                        sx={{ color: '#b0b0b0' }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewRecord(allRecords.find(r => r._id === selectedRowId))}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditRecord(selectedRowId)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Record</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteRecord(selectedRowId)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Record</ListItemText>
        </MenuItem>
      </Menu>

      {/* Record Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#24252a', color: '#ffffff' }
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Record Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Created by:</strong> {selectedRecord.createdBy?.username} ({selectedRecord.createdBy?.email})
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Scam Type:</strong> {selectedRecord.callDetails?.scamType}
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Amount:</strong> ${selectedRecord.callDetails?.totalAmount}
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Status:</strong> {selectedRecord.status}
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Description:</strong> {selectedRecord.callDetails?.description}
              </Typography>
              <Typography variant="body1" sx={{ color: '#ffffff', mb: 2 }}>
                <strong>Created:</strong> {format(new Date(selectedRecord.createdAt), 'MMM dd, yyyy HH:mm')}
              </Typography>
              
              {/* Drive Links Section */}
              {selectedRecord.driveLinks && selectedRecord.driveLinks.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <LinkIcon sx={{ mr: 1 }} />
                    Drive Links ({selectedRecord.driveLinks.length})
                  </Typography>
                  <List dense sx={{ bgcolor: '#1a1a1a', p: 0 }}>
                    {selectedRecord.driveLinks.map((link, index) => (
                      <ListItem key={index} sx={{ borderBottom: '1px solid #444' }}>
                        <ListItemIcon sx={{ minWidth: '40px' }}>
                          <LinkIcon sx={{ color: '#6464ff' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={link.title}
                          secondary={
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: '#6464ff', 
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': { color: '#8484ff' }
                              }}
                              onClick={() => window.open(link.url, '_blank')}
                            >
                              {link.url}
                            </Typography>
                          }
                          sx={{
                            '& .MuiListItemText-primary': { color: '#ffffff', fontSize: '0.875rem' },
                            '& .MuiListItemText-secondary': { color: '#bdbdbd', fontSize: '0.75rem' }
                          }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => window.open(link.url, '_blank')}
                            sx={{ color: '#6464ff' }}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Files Section (if any) */}
              {selectedRecord.files?.evidence && selectedRecord.files.evidence.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                    Attached Files ({selectedRecord.files.evidence.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedRecord.files.evidence.map((file, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #444' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Typography sx={{ fontSize: '1.5rem', mr: 1 }}>
                                {getFileIcon(file.mimeType)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#ffffff', flex: 1 }}>
                                {file.originalName || file.filename}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ color: '#b0b0b0', display: 'block', mb: 1 }}>
                              {file.mimeType}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                              File stored on server
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)} sx={{ color: '#ffffff' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;
